/**
 * Runs the light preview in place of the WebContainer (light-preview plan F2).
 *
 * The prototype is compiled in the tab and rendered from a blob document, so the founder's
 * browser starts no Node runtime, installs nothing, and runs no dev server. The file store drives
 * it: every write — the AI's, a hand edit, a click-to-edit patch — triggers a recompile.
 *
 * When the vendor bundle is missing (nobody ran `build:preview-vendor`, or a deploy shipped
 * without it) this reports `unavailable` and the caller boots the WebContainer instead, so a
 * missing build degrades to the old path rather than to a blank panel.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  appendTerminalLine,
  createPreviewCompiler,
  getHeadlessTemplateFiles,
  getMergedPreviewFiles,
  loadVendorAssets,
  switchTemplate,
  startLightPreview,
  tailwindConfigObjectSource,
  usePrototypeTemplate,
  workbenchStore,
  type LightPreview,
} from '@ideoz/workbench';

export type LightPreviewStatus = 'off' | 'starting' | 'running' | 'unavailable';

/**
 * The light preview is the default: it compiles in the tab, and when it cannot (no vendor
 * bundle in the deploy, an `esbuild.wasm` that will not load) it reports `unavailable` and the
 * WebContainer boots exactly as before. `VITE_WORKBENCH_PREVIEW=webcontainer` opts out of it
 * entirely, for a build that should never try.
 *
 * Vite inlines this at build time, so it is a property of the bundle, not of the running server.
 */
export const LIGHT_PREVIEW_REQUESTED = import.meta.env?.VITE_WORKBENCH_PREVIEW !== 'webcontainer';

/**
 * Whether a compile failure is shown to the founder. Off by default: the server build is what
 * judges a turn, and an esbuild message about a symbol declared twice is a builder's sentence in
 * the middle of a product. `VITE_WORKBENCH_PREVIEW_ERRORS=true` brings the panel back for
 * whoever is working on the preview itself.
 *
 * The preview behaves the same either way — a failed compile keeps the last version that
 * compiled on screen; this only decides whether it says so.
 */
export const PREVIEW_COMPILE_ERRORS_SHOWN =
  import.meta.env?.VITE_WORKBENCH_PREVIEW_ERRORS === 'true';

export interface LightPreviewRuntime {
  status: LightPreviewStatus;
  /** Why the light preview could not start; shown only in the fallback log, not to the founder. */
  unavailableReason: string | null;
}

/**
 * Resolves once the tab knows whether this conversation has a saved version. The restore needs
 * the workspace to be reported ready before it starts, so this can only be awaited after that.
 * The cap is a backstop: a restore that never settles must not leave the panel with no preview.
 */
function waitForRestoreDecision(timeoutMs = 15_000): Promise<void> {
  if (!workbenchStore.snapshotRestorePending.get()) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = window.setTimeout(finish, timeoutMs);
    const unsubscribe = workbenchStore.snapshotRestorePending.listen((pending) => {
      if (!pending) finish();
    });
  });
}

export function useLightPreviewRuntime(enabled: boolean): LightPreviewRuntime {
  // A new prototype opens with no files and turns mobile only when the build writes the seeded
  // Framework7 shell. The vendor bundle is loaded once per run of the effect below, so the
  // effect has to restart when the template changes - otherwise a mobile build keeps the web
  // bundle it started with, and every Framework7 import fails to resolve.
  const liveTemplate = usePrototypeTemplate();
  const [status, setStatus] = useState<LightPreviewStatus>('off');
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus('off');
      return;
    }

    let cancelled = false;
    let compiledOnce = false;
    let preview: LightPreview | null = null;
    setStatus('starting');
    workbenchStore.bootStatus.set('booting');
    workbenchStore.bootError.set(undefined);

    void (async () => {
      try {
        // The template has to be settled before anything is fetched: a mobile prototype needs
        // Framework7 in its import map and a web one needs Radix, and neither bundle carries the
        // other. Seeding the loader means every template-file lookup after this agrees.
        const template = liveTemplate;
        switchTemplate(template);
        const assets = await loadVendorAssets(undefined, undefined, template);
        const compiler = await createPreviewCompiler({
          wasmUrl: assets.wasmUrl,
          importMap: assets.importMap,
        });
        if (cancelled) return;

        // The Logs panel is otherwise the WebContainer's alone — "Mounting headless template",
        // "npx vite" — so an empty panel was the only sign that the tab preview was the one
        // running. Now it says so.
        appendTerminalLine(
          `Light preview ready — compiling in this tab, no Node runtime (dependencies ${assets.lockHash}).`,
          'info',
        );

        const tailwindConfig =
          tailwindConfigObjectSource(getHeadlessTemplateFiles()['tailwind.config.js'] ?? '') ??
          undefined;

        preview = startLightPreview({
          compiler,
          publisher: {
            setUrl: (url) => workbenchStore.previewUrl.set(url),
            setErrors: (errors) =>
              workbenchStore.previewCompileErrors.set(PREVIEW_COMPILE_ERRORS_SHOWN ? errors : []),
            setUnsupportedPackages: (packages) =>
              workbenchStore.previewUnsupportedPackages.set(packages),
            setCompiling: (compiling) => workbenchStore.previewCompiling.set(compiling),
          },
          getFiles: getMergedPreviewFiles,
          getAssets: () => workbenchStore.assets.get(),
          isMidTurn: () =>
            workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get(),
          subscribe: (onChange) => {
            // Images arrive on their own store, and a turn often writes them after the code
            // that references them.
            const unsubscribeFiles = workbenchStore.files.listen(() => onChange());
            const unsubscribeAssets = workbenchStore.assets.listen(() => onChange());
            // A turn ending is a reason to compile again even though no file changed: the last
            // compile ran mid-turn with its errors suppressed, and this one is allowed to
            // report them.
            const unsubscribeStreaming = workbenchStore.isStreaming.listen(() => onChange());
            return () => {
              unsubscribeFiles();
              unsubscribeAssets();
              unsubscribeStreaming();
            };
          },
          document: {
            importMap: assets.importMap,
            tailwindRuntimeUrl: assets.tailwindRuntimeUrl ?? undefined,
            tailwindConfig,
            // Framework7's look is a stylesheet, and the tab's compiler cannot resolve a bare
            // `.css` specifier — so a mobile prototype gets the vendored copy linked ahead of its
            // own CSS. A webapp prototype never imports Framework7 and never loads it (US 359).
            vendorStyleUrls: template === 'mobile'
              ? [
                  assets.packageStyleUrls.framework7,
                  // The icon fonts come with it: Framework7's bars and tabs are laid out around
                  // glyphs, and without them a prototype reads as unfinished.
                  assets.packageStyleUrls['framework7-icons'],
                  assets.packageStyleUrls['material-icons'],
                ].filter(Boolean)
              : undefined,
            // From the template, never the project, and after the project's styles: fixes to
            // Framework7's look reach prototypes built before them (28 Sep 2026).
            platformCss:
              template === 'mobile'
                ? getHeadlessTemplateFiles('mobile')['src/ideoz/platform.css']
                : undefined,
          },
          onCompiled: (result) => {
            if (cancelled || !result.js) return;
            if (!compiledOnce) {
              compiledOnce = true;
              appendTerminalLine(
                `Compiled the prototype in ${result.compileMs} ms.`,
                'info',
              );
            }
            workbenchStore.snapshotPreviewReady.set(true);
          },
        });

        // There is no dev server and nothing to install, so the workspace is ready as soon as
        // the compiler is. This has to be reported before the wait below, because the snapshot
        // restore only starts once the workspace says it is ready.
        workbenchStore.depsInstalled.set(true);
        workbenchStore.devServerStatus.set('ready');
        workbenchStore.bootStatus.set('ready');

        // Hold the first compile until the restore settles. Compiling now would put the starter
        // template on screen, and the panel reads a rendered starter page as "no saved version"
        // — which flashed that empty state on the way into every saved prototype.
        await waitForRestoreDecision();
        if (cancelled) return;

        await preview.refresh();
        if (cancelled) return;
        setStatus('running');
        setUnavailableReason(null);
      } catch (err) {
        if (cancelled) return;
        const reason = err instanceof Error ? err.message : String(err);
        console.warn('[Workbench] light preview unavailable; using the WebContainer', reason);
        // Also in the panel's own log, so "why is this booting a Node runtime?" is answerable
        // without a devtools console.
        appendTerminalLine(
          `Light preview unavailable (${reason}) — starting the in-browser Node runtime instead.`,
          'info',
        );
        setUnavailableReason(reason);
        setStatus('unavailable');
      }
    })();

    return () => {
      cancelled = true;
      preview?.stop();
      preview = null;
      workbenchStore.previewCompileErrors.set([]);
      workbenchStore.previewUnsupportedPackages.set([]);
      workbenchStore.previewCompiling.set(false);
    };
  }, [enabled, liveTemplate]);

  return useMemo(() => ({ status, unavailableReason }), [status, unavailableReason]);
}

/**
 * True while the light preview owns the panel: from the moment it is asked for until it reports
 * it cannot run, when the caller falls back to the WebContainer.
 *
 * `off` counts as owning, which looks odd but is the whole point. The runtime reports `starting`
 * from an effect, one commit after the panel opens, so on that first commit every status is
 * still `off` — and a caller that booted a WebContainer there would start a runtime the next
 * commit immediately tears down, taking the light preview's published document with it. The
 * caller pairs this with its own "is the workbench wanted" flag, so a panel nobody opened does
 * not count as owned.
 */
export function lightPreviewOwnsPreview(status: LightPreviewStatus): boolean {
  return status !== 'unavailable';
}
